// src/services/toolsAPI/fileMergeSplitAPI.ts
import axios from 'axios';
import tokenManager from '../../utils/tokenManager';

// Define the API endpoint paths - use localhost for development
const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8000';
const API_ROOT = `${API_BASE}/api`;
const TOOLS_API = `${API_ROOT}/tools/operations`;
const TEST_ENDPOINT = `${TOOLS_API}/test`;

// Define all paths as absolute URLs for consistency
const OPERATIONS_API = `${API_BASE}/api/tools/operations`;
const DIRECT_OPERATIONS_UPLOAD_API = `${API_BASE}/api/tools/operations-upload`;
const DIRECT_ROOT_UPLOAD = `${API_BASE}/direct-operations-upload`;

export interface FileDetails {
    name: string;
    size: number;
    type: string;
}

export interface UploadFilesResponse {
    fileIds: string[];
    fileDetails: FileDetails[];
}

export interface MergeResult {
    resultId: string;
    fileName: string;
    size: number;
    fileCount: number;
    outputFormat: string;
}

export interface MergeResponse {
    data: MergeResult | null;
    error: string | null;
}

export interface SplitResult {
    resultId: string;
    fileName: string;
    files: Array<{ name: string; size: number }>;
    parts: number;
}

export interface SplitResponse {
    data: SplitResult | null;
    error: string | null;
}

// Helper function to detect if we should use demo mode
const shouldUseDemoMode = (): boolean => {
    // Only use demo mode if explicitly enabled via env var in development
    const useDemoMode =
        import.meta.env.MODE === 'development' && import.meta.env.VITE_USE_DEMO === 'true';
    return useDemoMode;
};

// Add this function near the top of the file, after imports
const isDemoMode = (): boolean => {
    // Check if demo mode is enabled
    return (
        import.meta.env.VITE_DEMO_MODE === 'true' || window.location.search.includes('demo=true')
    );
};

// Create a demo merge response
const createDemoMergeResponse = (fileCount = 2): MergeResponse => {
    return {
        data: {
            resultId: `demo-merge-${Date.now()}`,
            fileName: 'merged_document.pdf',
            size: 1024 * 1024 * (fileCount / 2), // Size proportional to file count
            fileCount: fileCount,
            outputFormat: 'pdf',
        },
        error: null,
    };
};

// Create a demo split response
const createDemoSplitResponse = (): SplitResponse => {
    return {
        data: {
            resultId: `demo-split-${Date.now()}`,
            fileName: 'split_parts.zip',
            files: [
                { name: 'part1.pdf', size: 1024000 },
                { name: 'part2.pdf', size: 750000 },
            ],
            parts: 2,
        },
        error: null,
    };
};

/**
 * Helper function to check if the backend server is reachable
 */
export const checkBackendStatus = async (): Promise<boolean> => {
    try {
        // First try with the API root endpoint
        try {
            const rootResponse = await fetch(API_ROOT, {
                method: 'GET',
                headers: { 'Cache-Control': 'no-cache' },
            });

            // If we get any response from the root endpoint, even an error, it means the server is up
            if (rootResponse.status) {
                return true;
            }
        } catch (rootError) {
            // Continue with operations endpoint check
        }

        // Try with the operations test endpoint
        try {
            const response = await fetch(TEST_ENDPOINT, {
                method: 'GET',
                headers: { 'Cache-Control': 'no-cache' },
            });

            // Important: A 405 error means the endpoint exists but method is not allowed
            // This actually indicates the server is running properly!
            const isBackendOnline = response.ok || response.status === 405;

            if (isBackendOnline) {
                return true;
            }
        } catch (testError) {
            // Continue to final check
        }

        // Last resort - try the upload endpoint with OPTIONS to see if it exists
        try {
            const uploadResponse = await fetch(`${TOOLS_API}/upload`, {
                method: 'OPTIONS',
                headers: { 'Cache-Control': 'no-cache' },
            });

            // If we get any response, the server is probably up
            if (uploadResponse.status) {
                return true;
            }
        } catch (uploadError) { }

        // If we got here, the backend is not available
        if (import.meta.env.MODE === 'development' && typeof window !== 'undefined') {
            console.error(
                'BACKEND ERROR: Failed to connect to backend server. Please ensure your backend is running.'
            );
        }

        return false;
    } catch (error) {
        console.error('BACKEND CHECK: Backend server is not available:', error);

        if (import.meta.env.MODE === 'development' && typeof window !== 'undefined') {
            console.error(
                'BACKEND ERROR: Failed to connect to backend server. Please ensure your backend is running.'
            );
        }

        return false;
    }
};

/**
 * Upload files to the server for processing
 * @param files - Array of File objects to upload
 * @param resetCache - Whether to clear any cached data before upload
 * @returns Object with uploaded file IDs
 */
export const uploadFiles = async (
    files: File[],
    resetCache = true
): Promise<UploadFilesResponse> => {
    // Check if we're in demo mode
    if (shouldUseDemoMode() || isDemoMode()) {
        const mockIds = files.map((_, index) => `demo-file-${Date.now()}-${index}`);
        return {
            fileIds: mockIds,
            fileDetails: files.map((f) => ({ name: f.name, size: f.size, type: f.type })),
        };
    }

    // For development mode - skip backend checks if param is set
    const skipChecks = import.meta.env.VITE_SKIP_BACKEND_CHECK === 'true';

    // Check if backend is available (unless skipChecks is true)
    if (!skipChecks) {
        try {
            const backendStatus = await checkBackendStatus();
            if (!backendStatus) {
                const mockIds = files.map((_, index) => `demo-file-${Date.now()}-${index}`);
                return {
                    fileIds: mockIds,
                    fileDetails: files.map((f) => ({ name: f.name, size: f.size, type: f.type })),
                };
            }
        } catch (checkError) {
            console.error('UPLOAD DEBUG: Backend check failed:', checkError);
            // Continue anyway - will try direct uploads
        }
    }

    try {
        const formData = new FormData();

        // Add all files to the FormData
        files.forEach((file) => {
            formData.append(`files`, file);
        });

        // Add file_count parameter - this is required by the backend
        formData.append('file_count', files.length.toString());

        // Add reset cache flag to the request
        if (resetCache) {
            formData.append('reset_cache', 'true');
        }

        // Add timestamp to avoid caching
        formData.append('t', Date.now().toString());

        // Define all endpoints to try in sequence
        const endpoints = [
            { url: DIRECT_ROOT_UPLOAD, name: 'direct root' },
            { url: DIRECT_OPERATIONS_UPLOAD_API, name: 'direct operations' },
            { url: `${OPERATIONS_API}/upload`, name: 'operations API' },
            { url: '/api/tools/operations/upload', name: 'relative operations API' },
        ];

        // Try each endpoint in sequence
        for (const endpoint of endpoints) {
            try {
                const securityHeaders = (await tokenManager.getSecurityHeader()) || {};
                const response = await fetch(endpoint.url, {
                    method: 'POST',
                    headers: securityHeaders as HeadersInit,
                    body: formData,
                });

                if (response.ok) {
                    const data = await response.json();

                    if (data && data.fileIds && Array.isArray(data.fileIds)) {
                        return {
                            fileIds: data.fileIds,
                            fileDetails:
                                data.fileDetails || files.map((f) => ({ name: f.name, size: f.size, type: f.type })),
                        };
                    }
                }
            } catch (endpointError) {
                console.error(`UPLOAD ERROR: ${endpoint.name} upload error:`, endpointError);
            }
        }

        // If we get here, all endpoints failed
        console.error('UPLOAD ERROR: All endpoints failed');

        // In development, fall back to demo mode
        if (import.meta.env.MODE === 'development') {
            const mockIds = files.map((_, index) => `demo-file-${Date.now()}-${index}`);
            return {
                fileIds: mockIds,
                fileDetails: files.map((f) => ({ name: f.name, size: f.size, type: f.type })),
            };
        }

        throw new Error('Failed to upload files - all endpoints failed');
    } catch (error) {
        console.error('UPLOAD ERROR:', error);
        throw error;
    }
};

/**
 * Merge multiple files into a single file
 * @param fileIds - Array of file IDs to merge
 * @param options - Options for merging
 * @returns Object with merge result or error
 */
export const mergeFiles = async (fileIds: string[], options: any): Promise<MergeResponse> => {
    if (!fileIds || !Array.isArray(fileIds) || fileIds.length === 0) {
        console.error('MERGE ERROR - Invalid or missing fileIds:', fileIds);
        return {
            data: null,
            error: 'No valid file IDs provided for merge operation',
        };
    }

    try {
        // Check if we're using demo mode
        if (shouldUseDemoMode() || isDemoMode()) {
            await new Promise((resolve) => setTimeout(resolve, 1000));
            return createDemoMergeResponse(fileIds.length);
        }

        // Format request data according to backend expectations
        const requestData = {
            file_ids: fileIds,
            options: options,
        };

        // Try multiple endpoint variations to handle potential backend inconsistencies
        const endpoints = [
            `${OPERATIONS_API}/merge`,
            `${OPERATIONS_API}/merge_files`,
            `${API_BASE}/api/tools/merge`,
        ];

        let lastError = null;

        // Try each endpoint in sequence
        for (const endpoint of endpoints) {
            try {
                const securityHeaders = (await tokenManager.getSecurityHeader()) || {};
                const response = await fetch(endpoint, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        ...(securityHeaders as any),
                    },
                    body: JSON.stringify(requestData),
                });

                // Handle success or formatted error
                if (response.ok) {
                    const data = await response.json();
                    return { data, error: null };
                } else {
                    const errorText = await response.text();
                    lastError = `Server responded with ${response.status}: ${errorText}`;
                }
            } catch (error: any) {
                lastError = error.message;
            }
        }

        throw new Error(lastError || 'Failed to merge files');
    } catch (error: any) {
        console.error('MERGE ERROR:', error);

        // In development, return mock data if all endpoints fail
        if (import.meta.env.MODE === 'development') {
            return createDemoMergeResponse(fileIds.length);
        }

        return {
            data: null,
            error: error.message || 'Failed to merge files',
        };
    }
};

/**
 * Split a file into multiple parts
 * @param fileId - ID of the file to split
 * @param options - Options for splitting
 * @returns Object with split result or error
 */
export const splitFile = async (fileId: string, options: any): Promise<SplitResponse> => {
    if (!fileId) {
        console.error('SPLIT ERROR - Invalid or missing fileId:', fileId);
        return {
            data: null,
            error: 'No valid file ID provided for split operation',
        };
    }

    try {
        // Check if we're using demo mode
        if (shouldUseDemoMode() || isDemoMode()) {
            await new Promise((resolve) => setTimeout(resolve, 1000));
            return createDemoSplitResponse();
        }

        // Backend expects file_id as a query parameter
        const baseUrl = `${OPERATIONS_API}/split`;
        const url = new URL(baseUrl);
        url.searchParams.append('file_id', fileId);

        // Format request body with just the options
        const requestBody = {
            options: options,
        };

        const securityHeaders = (await tokenManager.getSecurityHeader()) || {};
        const response = await fetch(url.toString(), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(securityHeaders as any),
            },
            body: JSON.stringify(requestBody),
        });

        if (!response.ok) {
            const errorText = await response.text();
            console.error('SPLIT ERROR:', errorText);
            throw new Error(`Server responded with ${response.status}: ${errorText}`);
        }

        const data = await response.json();

        return {
            data,
            error: null,
        };
    } catch (error: any) {
        console.error('SPLIT ERROR:', error);

        // In development, return mock data if all endpoints fail
        if (import.meta.env.MODE === 'development') {
            return createDemoSplitResponse();
        }

        return {
            data: null,
            error: error.message || 'Failed to split file',
        };
    }
};

/**
 * Gets the download URL for a processed file
 * @param resultId - The ID of the result to download
 * @returns The download URL
 */
export const getDownloadLink = (resultId: string): string => {
    if (resultId.startsWith('demo-')) {
        return URL.createObjectURL(
            new Blob(['Demo file fusion merged/split content'], { type: 'application/pdf' })
        );
    }
    // Return absolute URL
    return `${OPERATIONS_API}/download/${resultId}`;
};
